import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaTag,
  FaLink,
  FaImage,
  FaArrowsAlt,
  FaEye,
  FaPalette,
  FaGlobe,
  FaInfoCircle,
  FaQuoteRight
} from 'react-icons/fa';

const HTMLLesson8 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  // Code examples
  const code1 = `<a href="https://codeflarelimited.com">Codefussion</a>`;
  const code2 = `<img src="truck.png" alt="A truck" />`;
  const code3 = `<img src="truck.png" width="450" height="450" />`;
  const code4 = `<img src="truck.png" alt="A truck" width="450" height="450" />`;
  const code5 = `<p style="color:red">This is it.</p>`;
  const code6 = `<!DOCTYPE html5>
<html lang="en-US">
<head> ... </head>
<body> ... </body>
</html>`;
  const code7 = `<p title="I am an attribute">This is a paragraph</p>`;
  const code8 = `<a href=https://codefussion.tech>Start Learning</a>`;
  const code9 = `<a href="https://codefussion.tech">Start Learning</a>`;
  const code10 = `<p title='Sunday Nwachukwu'>Sunday Nwachukwu</p>`;
  const code11 = `<p title="Sunday Nwachukwu">Sunday Nwachukwu</p>`;

  const sections = [
    {
      title: "HTML Attributes Introduction",
      content: "Attributes provide additional information about HTML elements and are essential for element functionality",
      keyPoints: [
        "All HTML elements can have attributes",
        "Attributes provide additional information about elements",
        "Attributes are always specified in the start tag",
        "Attributes usually come in name/value pairs like: name='value'"
      ],
      icon: <FaTag className="section-icon" />,
      color: "#2563eb",
      code: null
    },
    {
      title: "The href Attribute", 
      content: "The href attribute specifies the URL destination for links and is used with the <a> tag",
      keyPoints: [
        "Defines the link destination for <a> elements",
        "Can point to external websites or internal pages",
        "Essential for navigation and user experience",
        "Supports both absolute and relative URLs"
      ],
      icon: <FaLink className="section-icon" />,
      color: "#1d4ed8",
      code: code1
    },
    {
      title: "The src Attribute",
      content: "The src attribute specifies the source file path for images and other media elements",
      keyPoints: [
        "Used with <img> tags to specify image source",
        "Can reference local files or external URLs",
        "Required for images to display properly",
        "Supports various image formats (PNG, JPG, SVG, etc.)"
      ],
      icon: <FaImage className="section-icon" />,
      color: "#1e40af",
      code: code2
    },
    {
      title: "Width & Height Attributes",
      content: "These attributes control the display dimensions of images and other elements",
      keyPoints: [
        "Specify width and height in pixels",
        "Help browsers reserve space before image loads",
        "Maintain aspect ratio when only one dimension is set",
        "Improve page loading performance"
      ],
      icon: <FaArrowsAlt className="section-icon" />,
      color: "#7c3aed",
      code: code3
    },
    {
      title: "The alt Attribute",
      content: "The alt attribute provides alternative text for images when they cannot be displayed",
      keyPoints: [
        "Essential for accessibility (screen readers)",
        "Displays when images fail to load",
        "Important for SEO and image indexing",
        "Describes image content and function"
      ],
      icon: <FaEye className="section-icon" />,
      color: "#6b21a8",
      code: code4
    },
    {
      title: "The style Attribute",
      content: "The style attribute allows inline CSS styling for individual HTML elements",
      keyPoints: [
        "Applies CSS directly to elements",
        "Overrides external and internal styles",
        "Useful for quick, element-specific styling",
        "Follows CSS property:value syntax"
      ],
      icon: <FaPalette className="section-icon" />,
      color: "#059669",
      code: code5
    },
    {
      title: "The lang Attribute",
      content: "The lang attribute declares the language of the document for accessibility and SEO",
      keyPoints: [
        "Specifies document language in <html> tag",
        "Important for screen readers and search engines",
        "Uses language codes (en, es, fr, etc.)",
        "Can include country codes (en-US, en-GB)"
      ],
      icon: <FaGlobe className="section-icon" />,
      color: "#dc2626",
      code: code6
    },
    {
      title: "The title Attribute",
      content: "The title attribute provides additional information that appears as a tooltip",
      keyPoints: [
        "Displays tooltip on mouse hover",
        "Useful for providing extra context",
        "Works with most HTML elements",
        "Enhances user experience with hints"
      ],
      icon: <FaInfoCircle className="section-icon" />,
      color: "#ea580c",
      code: code7
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 8 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Attributes</h1>
              <p className="html-lesson-subtitle">
                Master HTML attributes to enhance element functionality, accessibility, and styling
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ').slice(0, 2).join(' ')}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index)}
                className={`html-content-card ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index) ? '✓ Viewed' : `Attribute ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content" dangerouslySetInnerHTML={{ __html: section.content }} />
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx} dangerouslySetInnerHTML={{ __html: point }} />
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for sections that have code */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaQuoteRight className="html-badge-icon" />
                Attribute Best Practices
              </div>
              <h3 className="html-practices-title">
                Quoting & Syntax Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid Unquoted Attributes</h4>
                <div className="html-code-example-bad">
                  <CopyBlock
                    language={language}
                    text={code8}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <p><strong>Problems:</strong> May break with spaces, special characters, or certain values</p>
              </div>
              
              <div className="html-practice-card html-do">
                <h4>✅ Use Double Quotes</h4>
                <div className="html-code-example-good">
                  <CopyBlock
                    language={language}
                    text={code9}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <p><strong>Benefits:</strong> Consistent, reliable, and follows web standards</p>
              </div>

              <div className="html-practice-card html-do">
                <h4>✅ Single Quotes When Needed</h4>
                <div className="html-code-example-good">
                  <CopyBlock
                    language={language}
                    text={code10}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <p><strong>Use when:</strong> Attribute value contains double quotes</p>
              </div>

              <div className="html-practice-card html-do">
                <h4>✅ Double Quotes Standard</h4>
                <div className="html-code-example-good">
                  <CopyBlock
                    language={language}
                    text={code11}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <p><strong>Best Practice:</strong> Use double quotes consistently throughout your code</p>
              </div>
            </div>
          </div>

          {/* Attribute Reference Table */}
          <div 
            className="html-attributes-reference"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-attributes-header">
              <div className="html-code-badge">
                <FaTag className="html-badge-icon" />
                Common HTML Attributes
              </div>
              <h3 className="html-attributes-title">
                Quick Reference Guide
              </h3>
            </div>
            
            <div className="html-attributes-table">
              <table>
                <thead>
                  <tr>
                    <th>Attribute</th>
                    <th>Description</th>
                    <th>Common Elements</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>href</code></td>
                    <td>Specifies the URL for links</td>
                    <td><code>&lt;a&gt;</code></td>
                  </tr>
                  <tr>
                    <td><code>src</code></td>
                    <td>Specifies the image source</td>
                    <td><code>&lt;img&gt;</code></td>
                  </tr>
                  <tr>
                    <td><code>alt</code></td>
                    <td>Alternative text for images</td>
                    <td><code>&lt;img&gt;</code></td>
                  </tr>
                  <tr>
                    <td><code>width/height</code></td>
                    <td>Element dimensions</td>
                    <td><code>&lt;img&gt;, &lt;table&gt;, &lt;canvas&gt;</code></td>
                  </tr>
                  <tr>
                    <td><code>style</code></td>
                    <td>Inline CSS styling</td>
                    <td>All elements</td>
                  </tr>
                  <tr>
                    <td><code>class</code></td>
                    <td>CSS class selector</td>
                    <td>All elements</td>
                  </tr>
                  <tr>
                    <td><code>id</code></td>
                    <td>Unique element identifier</td>
                    <td>All elements</td>
                  </tr>
                  <tr>
                    <td><code>title</code></td>
                    <td>Tooltip information</td>
                    <td>Most elements</td>
                  </tr>
                  <tr>
                    <td><code>lang</code></td>
                    <td>Language declaration</td>
                    <td><code>&lt;html&gt;</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                HTML Document with Multiple Attributes
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html lang="en-US">
<head>
    <title>HTML Attributes Example</title>
</head>
<body>
    <!-- Navigation with href attributes -->
    <nav>
        <a href="#home" title="Go to homepage">Home</a>
        <a href="#about" title="Learn about us">About</a>
        <a href="#contact" title="Get in touch">Contact</a>
    </nav>

    <!-- Image with multiple attributes -->
    <img 
        src="website-logo.png" 
        alt="Company Logo" 
        width="200" 
        height="100" 
        title="Our Company Logo"
    />

    <!-- Styled content -->
    <div class="container" id="main-content">
        <h1 style="color: blue; font-size: 2rem;">Welcome to Our Site</h1>
        
        <p title="Introduction paragraph">
            This is a sample paragraph with a title attribute that shows on hover.
        </p>

        <!-- Form elements with attributes -->
        <form action="/submit" method="post">
            <input 
                type="text" 
                name="username" 
                placeholder="Enter your username" 
                required
            >
            <input 
                type="email" 
                name="email" 
                placeholder="your@email.com" 
                required
            >
            <button type="submit" style="background-color: green; color: white;">
                Submit Form
            </button>
        </form>
    </div>

    <!-- External link -->
    <footer>
        <p>Visit our <a href="https://codeflarelimited.com" target="_blank">partner site</a></p>
    </footer>
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML attributes?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned about essential HTML attributes and their proper usage. Start implementing these in your web projects!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-id-selectors" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson8;